import { NavLink, Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { BrandMark } from './components/BrandMark';
import { useIsAuthenticated, useMsal } from '@azure/msal-react';
import { AUTH_MODE, DEV_USERS, devUser, loginRequest, setDevUser } from './auth';
import { useMe, useCan } from './hooks';
import { Icon, Loading, ErrorNote, Pill, ToastHost } from './components/ui';
import { Overview } from './pages/Overview';
import { People, PersonPage } from './pages/People';
import { Checklists } from './pages/Checklists';
import { Documents, DocumentPage } from './pages/Documents';
import { MyDocuments, MyOnboarding } from './pages/Mine';
import { Settings } from './pages/Settings';
import { Templates } from './pages/Templates';
import { TemplatePrepare } from './pages/TemplatePrepare';
import { PublicSign } from './pages/PublicSign';

export function App() {
  const loc = useLocation();
  if (loc.pathname.startsWith('/sign/')) return <Routes><Route path="/sign/:token" element={<PublicSign />} /></Routes>;
  return AUTH_MODE === 'dev' ? <Authed /> : <EntraGate />;
}

function EntraGate() {
  const authed = useIsAuthenticated();
  const { instance } = useMsal();
  if (authed) return <Authed />;
  return (
    <div className="signin">
      <div className="signin-card">
        <div className="brand dark"><BrandMark size="lg" /><span className="brand-t"><strong>PTI People</strong><span>Perth Training Institute · RTO 52730</span></span></div>
        <h1>Sign in</h1>
        <p className="small dim">Use your PTI Microsoft account. New starters: use the Temporary Access Pass we emailed you, then set up Microsoft Authenticator when asked.</p>
        <button className="btn primary big" onClick={() => void instance.loginRedirect(loginRequest)}><Icon n="lock" s={16} /> Sign in with Microsoft</button>
      </div>
    </div>
  );
}

function Authed() {
  const me = useMe();
  const can = useCan();
  const { instance } = useMsal();
  if (me.isLoading) return <div className="signin"><Loading /></div>;
  if (me.error) return <div className="signin"><div className="signin-card"><ErrorNote error={me.error} /></div></div>;
  const staff = can('people:read');
  const nav = staff
    ? [
      { to: '/', label: 'Overview', icon: 'home', end: true },
      { to: '/people', label: 'People', icon: 'users' },
      { to: '/checklists', label: 'On/offboarding', icon: 'check' },
      { to: '/documents', label: 'Documents', icon: 'doc' },
      ...(can('templates:manage') ? [{ to: '/templates', label: 'Templates', icon: 'doc' }] : []),
      ...(can('settings:checklists') ? [{ to: '/settings', label: 'Settings', icon: 'gear' }] : []),
      { to: '/me/documents', label: 'My documents', icon: 'user', sep: true },
    ]
    : [
      { to: '/me', label: 'My checklist', icon: 'check', end: true },
      { to: '/me/documents', label: 'My documents', icon: 'doc' },
    ];
  return (
    <div className="shell">
      <aside className="side">
        <div className="brand"><BrandMark onDark /><span className="brand-t"><strong>PTI People</strong><span>RTO 52730</span></span></div>
        <nav className="nav" aria-label="Main">
          {nav.map((n) => (
            <NavLink key={n.to} to={n.to} end={'end' in n ? n.end : false} className={({ isActive }) => `nav-i${isActive ? ' on' : ''}${'sep' in n && n.sep ? ' sep' : ''}`}>
              <Icon n={n.icon} s={17} /><span>{n.label}</span>
            </NavLink>
          ))}
        </nav>
        <div className="side-foot"><div className="safer">{['S', 'A', 'F', 'E', 'R'].map((k) => <span key={k}>{k}</span>)}</div><span>Safety · Accountability · Forward-thinking · Excellence · Respect</span></div>
      </aside>
      <div className="main">
        <header className="top">
          <span className="whoami small"><Icon n="user" s={14} /> {me.data!.user.name} <span className="dim">· {me.data!.user.upn}</span>
            {AUTH_MODE === 'entra' && <button className="link small" onClick={() => void instance.logoutRedirect()}>Sign out</button>}
          </span>
          {AUTH_MODE === 'dev' && (
            <div className="viewas">
              <Pill tone="proto">Local development</Pill>
              <div className="seg" role="radiogroup" aria-label="Sign in as">
                {DEV_USERS.map((u, i) => <button key={u.oid} role="radio" aria-checked={devUser().oid === u.oid} className={devUser().oid === u.oid ? 'on' : ''} onClick={() => setDevUser(i)}>{u.label}</button>)}
              </div>
            </div>
          )}
        </header>
        <main>
          <Routes>
            {staff ? <Route path="/" element={<Overview />} /> : <Route path="/" element={<Navigate to="/me" replace />} />}
            <Route path="/people" element={<People />} />
            <Route path="/people/:id" element={<PersonPage />} />
            <Route path="/checklists" element={<Checklists />} />
            <Route path="/checklists/:id" element={<Checklists />} />
            <Route path="/documents" element={<Documents />} />
            <Route path="/documents/:id" element={<DocumentPage />} />
            <Route path="/templates" element={<Templates />} />
            <Route path="/templates/new" element={<TemplatePrepare />} />
            <Route path="/templates/:key/prepare" element={<TemplatePrepare />} />
            <Route path="/settings" element={<Settings />} />
            <Route path="/me" element={<MyOnboarding />} />
            <Route path="/me/documents" element={<MyDocuments />} />
            <Route path="*" element={<p className="empty">Page not found.</p>} />
          </Routes>
        </main>
      </div>
      <ToastHost />
    </div>
  );
}
